<template>
  <VaLayout class="auth-layout rd-auth-console">
    <template #content>
      <div v-if="isLoginRoute()" ref="backgroundRef" class="auth-layout__background" aria-hidden="true"></div>
      <div class="auth-layout__veil" :class="{ 'auth-layout__veil--animated': isLoginRoute() }" aria-hidden="true"></div>

      <main class="auth-layout__main">
        <RouterLink class="auth-layout__logo-link" to="/" aria-label="Visit homepage">
          <VuesticLogo class="auth-layout__logo" :height="34" start="#F4F7FB" end="#F3A64A" />
        </RouterLink>

        <div class="auth-layout__router-shell" :class="{ 'auth-layout__router-shell--login': isLoginRoute() }">
          <RouterView />
        </div>
      </main>
    </template>
  </VaLayout>
</template>

<script lang="ts" setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import type { VantaEffect } from 'vanta/dist/vanta.net.min'
import VuesticLogo from '../components/VuesticLogo.vue'

const route = useRoute()
const backgroundRef = ref<HTMLElement | null>(null)
const backgroundEffect = ref<VantaEffect | null>(null)

const isLoginRoute = () => route.name === 'login'

const destroyBackground = () => {
  backgroundEffect.value?.destroy()
  backgroundEffect.value = null
}

const syncBackground = async () => {
  await nextTick()

  if (!isLoginRoute() || !backgroundRef.value) {
    destroyBackground()
    return
  }

  destroyBackground()

  const [{ default: createNetBackground }, threeModule] = await Promise.all([
    import('vanta/dist/vanta.net.min'),
    import('three'),
  ])

  if (!isLoginRoute() || !backgroundRef.value) {
    return
  }

  backgroundEffect.value = createNetBackground({
    el: backgroundRef.value,
    THREE: threeModule,
    mouseControls: true,
    touchControls: true,
    gyroControls: false,
    minHeight: 200,
    minWidth: 200,
    scale: 1,
    scaleMobile: 1,
    backgroundColor: 0x07121b,
    color: 0xf3a64a,
    points: 11,
    maxDistance: 20,
    spacing: 18,
    showDots: true,
  })
}

onMounted(() => {
  void syncBackground()
})

watch(
  () => route.name,
  () => {
    void syncBackground()
  },
)

onBeforeUnmount(() => {
  destroyBackground()
})
</script>

<style scoped>
.auth-layout,
:deep(.va-layout),
:deep(.va-layout__content) {
  min-height: 100vh;
  background: #070b0f !important;
}

.auth-layout {
  position: relative;
  overflow: hidden;
}

.auth-layout__background,
.auth-layout__veil {
  position: fixed;
  inset: 0;
}

.auth-layout__background {
  z-index: 0;
  opacity: 0.2;
}

.auth-layout__veil {
  z-index: 1;
  pointer-events: none;
  background:
    linear-gradient(180deg, rgba(7, 11, 15, 0.2), rgba(7, 11, 15, 0.88)),
    repeating-linear-gradient(90deg, rgba(49, 215, 255, 0.035) 0 1px, transparent 1px 84px),
    repeating-linear-gradient(0deg, rgba(103, 240, 177, 0.026) 0 1px, transparent 1px 72px);
}

.auth-layout__veil--animated {
  background:
    linear-gradient(180deg, rgba(7, 11, 15, 0.06), rgba(7, 11, 15, 0.9)),
    linear-gradient(115deg, rgba(49, 215, 255, 0.12), transparent 36%, rgba(255, 200, 87, 0.08) 72%, transparent),
    repeating-linear-gradient(90deg, rgba(49, 215, 255, 0.04) 0 1px, transparent 1px 92px);
}

.auth-layout__main {
  position: relative;
  z-index: 2;
  display: flex;
  min-height: 100vh;
  width: min(100%, 460px);
  margin: 0 auto;
  padding: 32px 16px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.auth-layout__logo-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
}

.auth-layout__logo {
  filter: drop-shadow(0 16px 34px rgba(0, 0, 0, 0.35));
}

.auth-layout__router-shell {
  width: 100%;
}

.auth-layout__router-shell--login {
  width: 100%;
}

@media (max-width: 640px) {
  .auth-layout__main {
    width: 100%;
    padding: 24px 12px;
    justify-content: flex-start;
  }

  .auth-layout__logo-link {
    margin-bottom: 14px;
  }
}
</style>
